// src/components/Admin/ItemManager/index.jsx
import { useEffect, useState } from 'react';
import {
  Box, Tabs, Tab, Button, Paper, useTheme, useMediaQuery,
} from '@mui/material';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

import api from '../../../api/axios';

import ItemTable from './ItemTable';
import ItemFormDialog from './ItemFormDialog';
import ItemUsageLog from './ItemUsageLog';
import ItemTransactionLog from './ItemTransactionLog';
import { buildWsUrl } from '../../../utils/websocket';

const ItemManager = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [hasPendingAction, setHasPendingAction] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ✅ 모바일에서 "등록 버튼"과 "검색/필터" 폭을 동일하게 맞출 컨테이너 폭
  const MOBILE_MAX = 420;

  useEffect(() => {
    const ws = new WebSocket(buildWsUrl('/ws/admin/items/'));

    ws.onopen = () => console.log('✅ Admin WebSocket connected');
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'item.action_request') {
          console.log('🛎️ 새 쿠폰 승인 요청:', data);
          setHasPendingAction(true);
        }
      } catch (e) {
        console.error('❌ WebSocket message error:', e);
      }
    };
    ws.onclose = () => console.log('❌ Admin WebSocket closed');

    return () => ws.close();
  }, []);

  const fetchItems = () => {
    api.get('/api/rewards/item-admin/')
      .then((res) => setItems(res.data.items || []))
      .catch(() => alert('아이템 목록 불러오기 실패'));
  };

  useEffect(() => { fetchItems(); }, []);

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    if (newTab === 1) setHasPendingAction(false);
  };

  const handleAddItem = () => { setEditingItem(null); setFormOpen(true); };

  const handleEditItem = (item) => {
    setEditingItem({
      ...item,
      price: item.price ?? 0,
      stock: item.stock ?? 0,
      is_active: item.is_active ?? true,
      is_unlimited: item.is_unlimited ?? true,
      is_coupon: item.is_coupon ?? true,
      description: item.description ?? '',
    });
    setFormOpen(true);
  };

  const handleDeleteItem = (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    api.delete(`/api/rewards/item-admin/${id}/delete/`)
      .then(fetchItems)
      .catch(() => alert('삭제 실패'));
  };

  const handleToggleActive = (id, cur) => {
    api.patch(`/api/rewards/item-admin/${id}/update/`, { is_active: !cur })
      .then(fetchItems)
      .catch(() => alert('노출 상태 변경 실패'));
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          backgroundColor: '#f5f5f5',
          px: isMobile ? 1 : 2,
          overflowX: 'auto', // ✅ 탭만 가로 스크롤 허용
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile}
          allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              whiteSpace: 'nowrap',
              fontSize: isMobile ? '0.85rem' : '1rem',
              minWidth: isMobile ? 140 : 'auto',
              px: isMobile ? 1 : 2,
            },
          }}
        >
          <Tab icon={<Inventory2Icon />} iconPosition="start" label="아이템 목록" />
          <Tab
            icon={<AssignmentTurnedInIcon />}
            iconPosition="start"
            label="승인/환불"
            sx={hasPendingAction ? {
              color: 'error.main',
              animation: 'blinker 1.2s linear infinite',
            } : {}}
          />
          <Tab icon={<ShoppingCartCheckoutIcon />} iconPosition="start" label="구매 로그" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        // ✅ 모바일에서만 폭을 고정/중앙정렬해서 "등록 버튼" == "검색/필터바" 폭이 되게 함
        <Box
          sx={{
            ...(isMobile && {
              maxWidth: MOBILE_MAX,
              mx: 'auto',
            }),
          }}
        >
          <Button
            variant="contained"
            sx={{ mb: 2 }}
            fullWidth // 모바일/PC 모두 동일 폭 컨테이너 기준으로 꽉 차게
            onClick={handleAddItem}
          >
            쿠폰/아이템 등록
          </Button>

          <ItemTable
            items={items}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onToggleActive={handleToggleActive}
          />
        </Box>
      )}

      {tab === 1 && <ItemUsageLog />}
      {tab === 2 && <ItemTransactionLog />}

      <ItemFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        item={editingItem}
        onSaved={fetchItems}
      />
    </Box>
  );
};

export default ItemManager;
